/**
 * NodeSummaryCards — one node, as six mini cards: cameras, recording & live, latest alerts,
 * AI models, groups, and the machine's own health.
 *
 * These started life inside the cloud dashboard's per-site row, which made them unreachable
 * from the product where a single node IS the whole installation. A local operator was left
 * with flat count tiles while the cloud operator got the donut, the alert list and the load
 * meters for the very same node — so the cards moved here and both products render them.
 *
 * Deliberately data-in, navigation-out. Each app fetches through its own transport (local hits
 * its services directly; cloud tunnels to the node) and each owns what a card click means —
 * cloud has to SELECT the node before routing, local just routes — so this component receives
 * an already-normalised `data` bundle plus a `links` map, and knows nothing about either.
 *
 * A card with no entry in `links` renders as a plain panel rather than a dead button: that is
 * how a permission the reader lacks is expressed. Its data still shows if they can read it.
 */
import type { ComponentType, ReactNode } from 'react';
import {
  ServerStackIcon,
  FilmIcon,
  CpuChipIcon,
  FolderIcon,
  VideoCameraIcon,
  BellAlertIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import CameraStatusDonut, { type DonutSlice } from './CameraStatusDonut';

/**
 * Soft tinted background + solid text from a CSS-var colour.
 *
 * `bg-success/15` does NOT work here: the theme maps these tokens as full colour values
 * (`success: 'var(--success)'`), and Tailwind's opacity modifier can't decompose that into
 * channels — the rule silently produces nothing.
 */
const tint = (c: string) => ({
  backgroundColor: `color-mix(in srgb, ${c} 15%, transparent)`,
  color: c,
});

/** The six cards, in the order they read: what exists → what it's doing → what it found. */
export type NodeSummaryCardKey = 'cameras' | 'recording' | 'alerts' | 'models' | 'groups' | 'status';

/**
 * Host metrics, in the cloud node-registry's field names — the local app maps its
 * `/monitoring/system` payload into this shape so one card serves both.
 *
 * Every field is optional: a card says "—" for what a node didn't report rather than printing
 * a confident zero.
 */
export interface NodeSummaryMetrics {
  cameraCount?: number;
  /** Live subscriptions on the node's media path. Omitted → the row is hidden, not zeroed. */
  activeStreams?: number;
  uptimeSeconds?: number;
  cpuPercent?: number;
  memoryPercent?: number;
  memoryUsedMB?: number;
  memoryTotalMB?: number;
  storageUsedGB?: number;
  storageTotalGB?: number;
  /** Any truthy value means "metrics have arrived" — used to stop the status skeleton. */
  timestamp?: any;
}

export interface NodeSummaryData {
  cameras: any[];
  groups: any[];
  /** `/models/assignments/all` payload: { models, camera_assignments, group_assignments }. */
  assignments: any;
  /** Latest alerts, newest first. */
  alerts: any[];
  metrics?: NodeSummaryMetrics;
  loading?: boolean;
}

export interface NodeSummaryCardsProps {
  data: NodeSummaryData;
  /** Destination per card. Omit a key to render that card as a non-interactive panel. */
  links?: Partial<Record<NodeSummaryCardKey, string>>;
  /**
   * How to follow a link. Cloud selects the node first (the destination is node-scoped and
   * would otherwise read whichever site was previously selected); local just navigates.
   */
  onNavigate?: (path: string) => void;
  /** Extra note under the health card — e.g. "worst of 3 machines" on a multi-machine node. */
  statusNote?: ReactNode;
  className?: string;
}

/** Camera status → bucket. Anything unrecognised is offline. */
const CAMERA_BUCKETS: Record<string, 'online' | 'connecting' | 'error'> = {
  streaming: 'online',
  recording: 'online',
  connecting: 'connecting',
  error: 'error',
};

export default function NodeSummaryCards({
  data,
  links = {},
  onNavigate,
  statusNote,
  className = '',
}: NodeSummaryCardsProps) {
  const m = data.metrics || {};
  const cameras = data.cameras || [];
  const loading = !!data.loading;

  const go = (key: NodeSummaryCardKey) => {
    const path = links[key];
    if (!path || !onNavigate) return undefined;
    return () => onNavigate(path);
  };

  // ── Camera status buckets ──
  const camCounts = { online: 0, connecting: 0, error: 0, offline: 0 };
  for (const cam of cameras) camCounts[CAMERA_BUCKETS[cam.status] || 'offline'] += 1;
  // The ring's total is the sum of its own buckets, never metrics.cameraCount: if the camera
  // list failed to load, falling back to cameraCount would print "12 cameras" in the hole
  // above a legend that sums to 0.
  const cameraTotal = cameras.length;
  /** Node reports cameras but we couldn't read the list — say so, don't show zeros. */
  const cameraCountsUnavailable = cameras.length === 0 && num(m.cameraCount) > 0;

  const donutSlices: DonutSlice[] = [
    { key: 'online', label: 'Online', value: camCounts.online, color: 'var(--success)' },
    { key: 'connecting', label: 'Connecting', value: camCounts.connecting, color: 'var(--warning)' },
    { key: 'error', label: 'Error', value: camCounts.error, color: 'var(--destructive)' },
    { key: 'offline', label: 'Offline', value: camCounts.offline, color: 'var(--muted-foreground)' },
  ];

  // ── Recording / live ──
  // `is_recording` is the real signal: the backend derives it from a fresh 'in-progress' row in
  // `recording_segments`. Do NOT count `status === 'recording'` — `cameras.status` holds one
  // value and the pipeline only ever writes streaming/connecting/inactive/error to it, so a
  // recording camera reads 'streaming' there and the status check silently returns 0.
  const recordingCount = cameras.filter((c) => c.is_recording).length;
  const streamingCount = cameras.filter((c) => c.status === 'streaming').length;

  // ── AI models ──
  const a = data.assignments || {};
  const modelTotal = (a.models || []).length;
  const camAssignments = (a.camera_assignments || []).length;
  const groupAssignments = (a.group_assignments || []).length;
  /** Distinct models actually attached somewhere — "assigned" in the useful sense. */
  const modelsInUse = new Set(
    [...(a.camera_assignments || []), ...(a.group_assignments || [])].map((x: any) => x.model_id),
  ).size;

  // ── Groups ──
  const groups = data.groups || [];
  const groupNames = groups.slice(0, 3).map((g: any) => g.name).filter(Boolean);

  // ── Alerts ──
  const alerts = data.alerts || [];
  const unacked = alerts.filter((al) => !al.acknowledged && !al.acknowledged_by).length;

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 ${className}`}>
      {/* 1 — Camera status donut */}
      <MiniCard title="Cameras" icon={VideoCameraIcon} onClick={go('cameras')} loading={loading}>
        {cameraCountsUnavailable ? (
          <p className="text-[11px] text-muted-foreground">
            This node reports {num(m.cameraCount)} camera(s), but the list couldn't be read.
          </p>
        ) : (
          <CameraStatusDonut slices={donutSlices} total={cameraTotal} />
        )}
      </MiniCard>

      {/* 2 — Recording / live */}
      <MiniCard title="Recording & Live" icon={FilmIcon} onClick={go('recording')} loading={loading}>
        {/* Recording and Streaming are peers — neither is subordinate, so they get the same
            figure size rather than a hero + a small row. */}
        <HeroPair
          items={[
            { value: recordingCount, label: 'Recording', tone: 'var(--destructive)' },
            { value: streamingCount, label: 'Streaming', tone: 'var(--success)' },
          ]}
        />
        {m.activeStreams != null && (
          <div className="mt-2 space-y-1.5 border-t border-border pt-2">
            <Row label="Active streams" value={String(num(m.activeStreams))} />
          </div>
        )}
      </MiniCard>

      {/* 3 — Latest alerts */}
      <MiniCard
        title="Latest Alerts"
        icon={BellAlertIcon}
        badge={unacked > 0 ? `${unacked} new` : undefined}
        onClick={go('alerts')}
        loading={loading}
      >
        {alerts.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No recent alerts.</p>
        ) : (
          <ul className="space-y-1.5">
            {alerts.slice(0, 4).map((al, i) => (
              <li key={al.id || i} className="flex items-start gap-1.5 text-[11px] leading-tight">
                <span
                  className="mt-1 h-1.5 w-1.5 flex-none rounded-full"
                  style={{ backgroundColor: severityColor(al.severity) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-foreground" title={al.event_type || al.title}>
                  {al.event_type || al.title || 'Alert'}
                </span>
                <span className="flex-none text-muted-foreground">{formatAlertTime(al)}</span>
              </li>
            ))}
          </ul>
        )}
      </MiniCard>

      {/* 4 — AI models */}
      <MiniCard title="AI Models" icon={CpuChipIcon} onClick={go('models')} loading={loading}>
        <Hero value={modelTotal} caption={modelTotal === 1 ? 'model registered' : 'models registered'} />
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          <Row label="Attached" value={String(modelsInUse)} />
          <Row label="Camera rules" value={String(camAssignments)} />
          <Row label="Group rules" value={String(groupAssignments)} />
        </div>
      </MiniCard>

      {/* 5 — Groups */}
      <MiniCard title="Groups" icon={FolderIcon} onClick={go('groups')} loading={loading}>
        <Hero value={groups.length} caption={groups.length === 1 ? 'group' : 'groups'} />
        {groupNames.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1 border-t border-border pt-2">
            {/* bg-card, not bg-muted: the chips sit on a recessed tile, so the card surface is
                what makes them read as raised in both themes (`--muted` is nearly identical to
                `--background` in light mode). */}
            {groupNames.map((n: string) => (
              <span
                key={n}
                className="max-w-full truncate rounded border border-border bg-card px-1.5 py-0.5 text-[10px] text-muted-foreground"
              >
                {n}
              </span>
            ))}
            {groups.length > groupNames.length && (
              <span className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground">
                +{groups.length - groupNames.length}
              </span>
            )}
          </div>
        ) : (
          <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">No groups defined</p>
        )}
      </MiniCard>

      {/* 6 — Machine health */}
      <MiniCard title="System Health" icon={ServerStackIcon} onClick={go('status')} loading={loading && !m.timestamp}>
        <div className="space-y-1.5">
          <Row label="Uptime" value={formatUptime(m.uptimeSeconds)} icon={ClockIcon} />
          <Meter label="CPU" percent={num(m.cpuPercent)} />
          <Meter
            label="Memory"
            percent={num(m.memoryPercent)}
            hint={m.memoryUsedMB ? `${gb(m.memoryUsedMB)} / ${gb(m.memoryTotalMB)}` : undefined}
          />
          <Meter
            label="Disk"
            percent={pct(m.storageUsedGB, m.storageTotalGB)}
            hint={m.storageTotalGB ? `${Math.round(num(m.storageUsedGB))} / ${Math.round(num(m.storageTotalGB))} GB` : undefined}
          />
          {statusNote && <p className="pt-0.5 text-[10px] text-muted-foreground">{statusNote}</p>}
        </div>
      </MiniCard>
    </div>
  );
}

/* ─── Mini card chrome ─── */

/**
 * `group` + `group-hover:` is what lets the whole tile drive its own hover state — the arrow and
 * icon chip light up together rather than each needing its own handler. `min-h` equalises tiles
 * whose content is only 2–3 rows against the donut and the four meters, so the row isn't ragged.
 */
export function MiniCard({
  title,
  icon: Icon,
  children,
  onClick,
  badge,
  loading,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  onClick?: () => void;
  badge?: string;
  loading?: boolean;
}) {
  const interactive = !!onClick;
  return (
    <div
      // stopPropagation so a tile click doesn't ALSO fire an enclosing row's own handler — the
      // tile's destination must win.
      onClick={interactive ? (e) => { e.stopPropagation(); onClick?.(); } : undefined}
      // Keyboard parity: a div with onClick is unreachable by keyboard otherwise.
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onClick?.();
              }
            }
          : undefined
      }
      // `bg-background` recesses the tile into the row's `bg-card` sheet, and it works in both
      // themes: the page surface is lighter than the card in light mode and darker in dark.
      // Hover lifts the tile TO the card surface, so it reads as picked up off the sheet.
      className={`group flex min-h-[136px] flex-col rounded-lg border border-border bg-background p-3 transition-all ${
        interactive
          ? 'cursor-pointer hover:-translate-y-px hover:border-brand/50 hover:bg-card hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-brand-ring'
          : ''
      }`}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <span
          className="flex h-5 w-5 flex-none items-center justify-center rounded transition-colors"
          style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 12%, transparent)' }}
        >
          <Icon className="h-3.5 w-3.5 text-muted-foreground transition-colors group-hover:text-brand" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {badge && (
          <span
            className="flex-none rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none"
            style={tint('var(--destructive)')}
          >
            {badge}
          </span>
        )}
        {interactive && (
          <ArrowRightIcon className="h-3 w-3 flex-none text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:text-brand" />
        )}
      </div>
      {loading ? (
        <div className="flex-1 space-y-2">
          <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * The tile's headline figure. Gives the sparse tiles (recording, models, groups) something to
 * scan at the same visual weight as the donut, so no tile reads as filler. `tone` tints only the
 * small dot, never the number — see `Row`.
 */
function Hero({ value, caption, tone }: { value: number; caption: string; tone?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      {tone && value > 0 && (
        <span className="mb-1 h-2 w-2 flex-none self-end rounded-full" style={{ backgroundColor: tone }} aria-hidden />
      )}
      <span className="text-2xl font-bold leading-none tabular-nums text-foreground">{value}</span>
      <span className="min-w-0 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{caption}</span>
    </div>
  );
}

/**
 * Two figures at equal weight, for a tile where neither number is subordinate to the other. Same
 * numeral size as `Hero`, with the caption beneath instead of inline so two of them fit.
 *
 * The status dot renders even at 0, unlike `Hero` — the two columns must stay structurally
 * identical or "equal weight" breaks the moment one hits zero.
 */
function HeroPair({ items }: { items: { value: number; label: string; tone?: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((it) => (
        <div key={it.label} className="min-w-0">
          <div className="flex items-baseline gap-1.5">
            {it.tone && (
              <span
                className="mb-1 h-2 w-2 flex-none self-end rounded-full"
                style={{ backgroundColor: it.tone }}
                aria-hidden
              />
            )}
            <span className="text-2xl font-bold leading-none tabular-nums text-foreground">{it.value}</span>
          </div>
          <p className="mt-1 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{it.label}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Label + value line. `tone` puts a status dot BEFORE the label rather than colouring the
 * number: values stay in foreground ink so they're legible at any status, and the dot carries
 * the state — the same split the donut legend uses.
 */
function Row({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone?: string;
  icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {tone && <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: tone }} aria-hidden />}
      {Icon && <Icon className="h-3 w-3 flex-none text-muted-foreground" />}
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
      <span className="flex-none font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * A single ratio against a limit is a meter, not a chart — one track, one fill, value stated in
 * text so the bar is never the only channel.
 */
function Meter({ label, percent, hint }: { label: string; percent: number; hint?: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  // Load thresholds reuse the reserved status tokens, so a hot node reads the same way here as
  // it does on the monitor page.
  const color = clamped >= 90 ? 'var(--destructive)' : clamped >= 75 ? 'var(--warning)' : 'var(--success)';
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px]">
        <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
        {hint && <span className="flex-none text-[10px] tabular-nums text-muted-foreground/70">{hint}</span>}
        <span className="flex-none font-semibold tabular-nums text-foreground">
          {clamped ? `${Math.round(clamped)}%` : '—'}
        </span>
      </div>
      {/* Track is a mix off the ink colour, not `bg-muted`: muted against a recessed
          `bg-background` tile is nearly invisible in light mode. */}
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full"
        style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 20%, transparent)' }}
      >
        <div className="h-full rounded-full transition-all" style={{ width: `${clamped}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

/* ─── Formatting ─── */

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const gb = (mb: any) => `${(num(mb) / 1024).toFixed(1)}GB`;
const pct = (used: any, total: any) => (num(total) > 0 ? (num(used) / num(total)) * 100 : 0);

function severityColor(severity?: string): string {
  switch ((severity || '').toLowerCase()) {
    case 'critical': return 'var(--destructive)';
    case 'warning': return 'var(--warning)';
    default: return 'var(--info)';
  }
}

export function formatAlertTime(al: any): string {
  // CC alerts carry `event_timestamp` in epoch SECONDS; `created_at` is an ISO string.
  const ms = al.event_timestamp ? Number(al.event_timestamp) * 1000 : Date.parse(al.created_at || '');
  if (!Number.isFinite(ms)) return '—';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export function formatUptime(seconds: any): string {
  const s = num(seconds);
  if (s <= 0) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${mins}m`;
  return `${mins}m`;
}
